import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { toast, Toaster } from "sonner";
import { Users, Send } from "lucide-react";

export default function BulkOrderPage() {
    const navigate = useNavigate();
    const [form, setForm] = useState({
        company: "", contact_name: "", email: "", phone: "",
        quantity: 10, garment_type: "tshirt", desired_date: "",
        budget: "", message: "",
    });
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);

    const setF = (k) => (e) => setForm(prev => ({ ...prev, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await api.post("/bulk-inquiry", { ...form, quantity: parseInt(form.quantity) });
            setDone(true);
            toast.success("Aanvraag verzonden!");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Iets ging mis");
        } finally {
            setSubmitting(false);
        }
    };

    if (done) {
        return (
            <div data-testid="bulk-success" className="min-h-[60vh] grid place-items-center p-10 text-center bg-zinc-50">
                <div className="max-w-md">
                    <Users size={48} className="mx-auto text-emerald-600" />
                    <p className="label-tiny mt-4">Aanvraag ontvangen</p>
                    <h1 className="font-display text-4xl mt-2">Bedankt!</h1>
                    <p className="text-zinc-600 mt-3 leading-relaxed">
                        We hebben je aanvraag goed ontvangen. Binnen 24 uur krijg je een offerte op maat per e-mail.
                    </p>
                    <button onClick={() => navigate("/")} className="btn-primary mt-6">Terug naar home</button>
                </div>
            </div>
        );
    }

    return (
        <div data-testid="bulk-order-page" className="bg-zinc-50 min-h-screen">
            <Toaster />
            <div className="max-w-3xl mx-auto px-6 py-12">
                <p className="label-tiny flex items-center gap-1"><Users size={12} /> Bulkbestelling</p>
                <h1 className="font-display text-5xl mt-2">5+ stuks?<br />Vraag een offerte aan.</h1>
                <p className="text-zinc-600 mt-3 max-w-2xl leading-relaxed">
                    Voor clubs, bedrijven, sportploegen, vrijgezellenfeesten of teamuitjes hebben we speciale tarieven.
                    Vul onderstaand formulier in en je krijgt binnen 24 uur een offerte op maat.
                </p>

                <form onSubmit={submit} className="mt-10 card p-6 space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Field label="Bedrijf / Vereniging"><input data-testid="bulk-company" required value={form.company} onChange={setF("company")} className="input-flat" /></Field>
                        <Field label="Contactpersoon"><input data-testid="bulk-name" required value={form.contact_name} onChange={setF("contact_name")} className="input-flat" /></Field>
                        <Field label="E-mail"><input data-testid="bulk-email" type="email" required value={form.email} onChange={setF("email")} className="input-flat" /></Field>
                        <Field label="Telefoon"><input data-testid="bulk-phone" required value={form.phone} onChange={setF("phone")} className="input-flat" /></Field>
                        <Field label="Aantal stuks (min. 5)"><input data-testid="bulk-quantity" type="number" min={5} required value={form.quantity} onChange={setF("quantity")} className="input-flat" /></Field>
                        <Field label="Type kleding">
                            <select data-testid="bulk-type" value={form.garment_type} onChange={setF("garment_type")} className="input-flat">
                                <option value="tshirt">T-shirts</option>
                                <option value="hoodie">Hoodies</option>
                                <option value="cap">Petten</option>
                                <option value="beanie">Mutsen</option>
                                <option value="mixed">Gemengd</option>
                            </select>
                        </Field>
                        <Field label="Gewenste leverdatum (optioneel)"><input data-testid="bulk-date" type="date" value={form.desired_date} onChange={setF("desired_date")} className="input-flat" /></Field>
                        <Field label="Budget (optioneel)"><input data-testid="bulk-budget" placeholder="bv. €500-800" value={form.budget} onChange={setF("budget")} className="input-flat" /></Field>
                    </div>
                    <Field label="Bericht (designwensen, kleuren, deadlines, …)" wide>
                        <textarea data-testid="bulk-message" rows={5} required value={form.message} onChange={setF("message")} className="input-flat" />
                    </Field>
                    <button type="submit" disabled={submitting} data-testid="bulk-submit" className="btn-primary flex items-center gap-2">
                        <Send size={14} /> {submitting ? "Verzenden…" : "Vraag offerte aan"}
                    </button>
                </form>

                <div className="mt-8 border border-amber-200 bg-amber-50 p-5 text-sm">
                    <p className="font-semibold text-amber-900 mb-2">💡 Voordelen bij bulkbestellingen</p>
                    <ul className="space-y-1 text-amber-900">
                        <li>• Vanaf 5 stuks: tot 15% korting (automatisch via volume korting)</li>
                        <li>• Bij grote bestellingen: persoonlijke begeleiding bij design</li>
                        <li>• Gratis verzending vanaf €75 totaal</li>
                        <li>• Levering binnen 7-10 werkdagen mogelijk</li>
                    </ul>
                </div>
            </div>
        </div>
    );
}

function Field({ label, children, wide }) {
    return (
        <label className={`block ${wide ? "md:col-span-2" : ""}`}>
            <span className="label-tiny block mb-1">{label}</span>
            {children}
        </label>
    );
}
